<template>
  <div class="case-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2><i class="el-icon-document"></i> 官网案例管理</h2>
      <p class="page-desc">管理官网展示的成功案例，支持添加、编辑、删除案例</p>
    </div>

    <!-- 操作栏 -->
    <div class="action-bar">
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增案例</el-button>
    </div>

    <!-- 案例列表表格 -->
    <el-table
      :data="caseList"
      border
      stripe
      style="width: 100%"
      v-loading="loading"
      empty-text="暂无案例数据"
    >
      <el-table-column prop="id" label="ID" width="60" align="center"></el-table-column>
      <el-table-column prop="title" label="案例标题" min-width="150" show-overflow-tooltip></el-table-column>
      <el-table-column label="案例类型" width="130" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.type === '1' ? 'primary' : 'success'">
            {{ scope.row.type === '1' ? '政企招标项目' : scope.row.type === '2' ? '商业空间落地' : '-' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="projectName" label="项目名称" min-width="150" show-overflow-tooltip></el-table-column>
      <el-table-column prop="projectTime" label="项目时间" width="120" align="center"></el-table-column>
      <el-table-column prop="projectLocation" label="项目地点" width="120" show-overflow-tooltip></el-table-column>
      <el-table-column prop="projectUrl" label="项目地址" min-width="180" show-overflow-tooltip>
        <template slot-scope="scope">
          <a :href="scope.row.projectUrl" target="_blank" class="link-text" v-if="scope.row.projectUrl">
            {{ scope.row.projectUrl }}
          </a>
          <span v-else class="no-data">-</span>
        </template>
      </el-table-column>
      <el-table-column label="图片数" width="80" align="center">
        <template slot-scope="scope">
          <el-tag size="small" type="info">{{ (scope.row.images || []).length }}张</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" type="primary" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button size="mini" type="danger" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 新增/编辑案例弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :title="isEdit ? '编辑案例' : '新增案例'"
      width="800px"
      top="3vh"
      :close-on-click-modal="false"
      @closed="handleDialogClosed"
    >
      <el-form
        ref="caseForm"
        :model="formData"
        :rules="formRules"
        label-width="100px"
        label-position="right"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="案例标题" prop="title">
              <el-input v-model="formData.title" placeholder="请输入案例标题" maxlength="100" show-word-limit></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="项目名称" prop="projectName">
              <el-input v-model="formData.projectName" placeholder="请输入项目名称" maxlength="100" show-word-limit></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="案例类型" prop="type">
              <el-select v-model="formData.type" placeholder="请选择案例类型" style="width: 100%">
                <el-option label="政企招标项目" value="1"></el-option>
                <el-option label="商业空间落地" value="2"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="项目时间" prop="projectTime">
              <el-input v-model="formData.projectTime" placeholder="请输入项目时间，如：2024年6月" maxlength="50"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="项目地点" prop="projectLocation">
              <el-input v-model="formData.projectLocation" placeholder="请输入项目地点，如：四川成都" maxlength="100"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="副标题" prop="subtitle">
              <el-input v-model="formData.subtitle" placeholder="请输入案例副标题" maxlength="100" show-word-limit></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="设计单位" prop="designUnit">
              <el-input v-model="formData.designUnit" placeholder="请输入设计单位" maxlength="100"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="施工单位" prop="constructionUnit">
              <el-input v-model="formData.constructionUnit" placeholder="请输入施工单位" maxlength="100"></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="项目简介" prop="description">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="3"
            placeholder="请输入项目简介"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>

        <el-form-item label="案例图片" prop="images">
          <div class="image-upload-area">
            <div class="image-list">
              <div
                v-for="(img, index) in formData.images"
                :key="index"
                class="image-item"
              >
                <img :src="img.url" :alt="'图片' + (index + 1)" @error="onImgError($event)" />
                <div class="image-order-actions">
                  <el-button
                    size="mini"
                    icon="el-icon-arrow-up"
                    circle
                    :disabled="index === 0"
                    @click.stop="handleMoveUp(index)"
                    title="上移"
                  ></el-button>
                  <el-button
                    size="mini"
                    icon="el-icon-arrow-down"
                    circle
                    :disabled="index === formData.images.length - 1"
                    @click.stop="handleMoveDown(index)"
                    title="下移"
                  ></el-button>
                </div>
                <div class="image-actions">
                  <el-button
                    size="mini"
                    type="warning"
                    icon="el-icon-refresh"
                    circle
                    @click="handleReplaceImage(index)"
                    title="替换图片"
                  ></el-button>
                  <el-button
                    size="mini"
                    type="danger"
                    icon="el-icon-delete"
                    circle
                    @click="handleRemoveImage(index)"
                    title="删除图片"
                  ></el-button>
                </div>
                <span class="image-index">第 {{ index + 1 }} 张</span>
              </div>

              <!-- 添加图片占位 -->
              <div
                class="image-item image-add"
                @click="handleAddImage"
                v-if="formData.images.length < 9"
              >
                <i class="el-icon-plus"></i>
                <span>添加图片</span>
              </div>
            </div>
            <p class="image-tip">支持 JPG/PNG/GIF/WebP，单张不超过10MB，最多9张；图片将按 image1、image2... 命名存储</p>
          </div>
        </el-form-item>
      </el-form>

      <!-- 上传进度 -->
      <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress-bar">
        <el-progress :percentage="uploadProgress" :stroke-width="12" :text-inside="true"></el-progress>
        <p class="progress-text">正在上传图片 {{ currentUploadIndex + 1 }}/{{ totalUploadCount }}...</p>
      </div>

      <span slot="footer">
        <el-button @click="dialogVisible = false" :disabled="uploading">取 消</el-button>
        <el-button type="primary" :loading="uploading" @click="handleSubmit">
          {{ uploading ? '上传中...' : '确 定' }}
        </el-button>
      </span>
    </el-dialog>

    <!-- 隐藏的文件选择框 -->
    <input
      ref="fileInput"
      type="file"
      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
      style="display: none"
      @change="onFileSelected"
    />
  </div>
</template>

<script>
import { getCases, saveCase, updateCase, deleteCase } from '@/api/admin'
import { uploadCaseImage, deleteCaseFolder } from '@/utils/ossUpload'

export default {
  name: 'CaseManagement',
  data() {
    return {
      // 列表数据
      caseList: [],
      loading: false,

      // 弹窗控制
      dialogVisible: false,
      isEdit: false,
      editId: null,

      // 表单数据
      formData: {
        title: '',
        subtitle: '',
        type: '1',
        projectName: '',
        projectTime: '',
        projectLocation: '',
        projectUrl: '',
        designUnit: '',
        constructionUnit: '',
        description: '',
        images: []
      },

      // 表单校验
      formRules: {
        title: [
          { required: true, message: '请输入案例标题', trigger: 'blur' },
          { max: 100, message: '标题不能超过100个字符', trigger: 'blur' }
        ],
        type: [
          { required: true, message: '请选择案例类型', trigger: 'change' }
        ],
        projectName: [
          { max: 100, message: '项目名称不能超过100个字符', trigger: 'blur' }
        ],
        subtitle: [
          { max: 100, message: '副标题不能超过100个字符', trigger: 'blur' }
        ],
        projectLocation: [
          { max: 100, message: '项目地点不能超过100个字符', trigger: 'blur' }
        ],
        designUnit: [
          { max: 100, message: '设计单位不能超过100个字符', trigger: 'blur' }
        ],
        constructionUnit: [
          { max: 100, message: '施工单位不能超过100个字符', trigger: 'blur' }
        ],
        projectUrl: [
          { max: 500, message: '项目地址不能超过500个字符', trigger: 'blur' }
        ],
        description: [
          { max: 500, message: '简介不能超过500个字符', trigger: 'blur' }
        ]
      },

      // 上传相关
      uploading: false,
      uploadProgress: 0,
      currentUploadIndex: 0,
      totalUploadCount: 0,

      // 等待替换图片的索引
      pendingReplaceIndex: -1
    }
  },
  created() {
    this.loadCaseList()
  },
  methods: {
    /**
     * 加载案例列表
     */
    async loadCaseList() {
      this.loading = true
      try {
        const res = await getCases()

        // 服务器返回的可能是数组，也可能是 { data: [...] } 格式
        let list = []
        if (Array.isArray(res)) {
          list = res
        } else if (res && Array.isArray(res.data)) {
          list = res.data
        } else if (res && Array.isArray(res.list)) {
          list = res.list
        } else if (res && typeof res === 'object') {
          // 尝试从对象中获取第一个数组字段
          const arrayField = Object.values(res).find(v => Array.isArray(v))
          if (arrayField) list = arrayField
        }

        // 解析每个案例的JSON数据
        // 服务器返回格式: { ID, CreatedAt, DeletedAt, UpdatedAt, Data }
        // Data 字段是存储的案例JSON字符串
        this.caseList = list.map(item => {
          let caseData = {}
          if (typeof item.Data === 'string') {
            try {
              caseData = JSON.parse(item.Data)
            } catch (e) {
              caseData = {}
            }
          } else if (item.Data && typeof item.Data === 'object') {
            caseData = item.Data
          }
          return {
            id: item.ID,
            createdAt: item.CreatedAt,
            updatedAt: item.UpdatedAt,
            ...caseData,
            images: caseData.images || []
          }
        })
      } catch (error) {
        console.error('加载案例列表失败:', error)
        this.$message.error('加载案例列表失败')
      } finally {
        this.loading = false
      }
    },

    /**
     * 新增案例
     */
    handleAdd() {
      this.isEdit = false
      this.editId = null
      this.formData = this.getEmptyFormData()
      this.dialogVisible = true
    },

    /**
     * 编辑案例
     */
    handleEdit(row) {
      this.isEdit = true
      this.editId = row.id
      this.formData = {
        title: row.title || '',
        subtitle: row.subtitle || '',
        type: row.type || '1',
        projectName: row.projectName || '',
        projectTime: row.projectTime || '',
        projectLocation: row.projectLocation || '',
        projectUrl: row.projectUrl || '',
        designUnit: row.designUnit || '',
        constructionUnit: row.constructionUnit || '',
        description: row.description || '',
        images: (row.images || []).map(img => ({
          ...img,
          status: 'old', // 标记为旧图片
          file: undefined
        }))
      }
      this.dialogVisible = true
    },

    /**
     * 获取空表单数据
     */
    getEmptyFormData() {
      return {
        title: '',
        subtitle: '',
        type: '1',
        projectName: '',
        projectTime: '',
        projectLocation: '',
        projectUrl: '',
        designUnit: '',
        constructionUnit: '',
        description: '',
        images: []
      }
    },

    /**
     * 弹窗关闭回调
     */
    handleDialogClosed() {
      this.uploadProgress = 0
      this.currentUploadIndex = 0
      this.totalUploadCount = 0
      this.uploading = false
      this.pendingReplaceIndex = -1
    },

    /**
     * 添加图片
     */
    handleAddImage() {
      this.pendingReplaceIndex = -1
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },

    /**
     * 替换指定索引的图片
     */
    handleReplaceImage(index) {
      this.pendingReplaceIndex = index
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },

    /**
     * 上移图片
     */
    handleMoveUp(index) {
      if (index <= 0) return
      const img = this.formData.images[index]
      this.formData.images.splice(index, 1)
      this.formData.images.splice(index - 1, 0, img)
    },

    /**
     * 下移图片
     */
    handleMoveDown(index) {
      if (index >= this.formData.images.length - 1) return
      const img = this.formData.images[index]
      this.formData.images.splice(index, 1)
      this.formData.images.splice(index + 1, 0, img)
    },

    /**
     * 删除指定索引的图片
     */
    handleRemoveImage(index) {
      this.formData.images.splice(index, 1)
    },

    /**
     * 文件选择回调
     */
    onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return

      // 检查文件类型
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('请选择 PNG、JPG、GIF 或 WebP 格式的图片')
        return
      }

      // 检查文件大小（10MB）
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 10MB')
        return
      }

      const previewUrl = URL.createObjectURL(file)

      if (this.pendingReplaceIndex >= 0) {
        // 替换已有图片，保持imageIndex不变
        const existingImg = this.formData.images[this.pendingReplaceIndex]
        this.formData.images[this.pendingReplaceIndex] = {
          url: previewUrl,
          fileName: existingImg ? existingImg.fileName : '',
          status: 'new',
          file: file,
          imageIndex: existingImg ? existingImg.imageIndex : (this.pendingReplaceIndex + 1)
        }
      } else {
        // 新增图片
        this.formData.images.push({
          url: previewUrl,
          fileName: '',
          status: 'new',
          file: file,
          imageIndex: this.formData.images.length + 1
        })
      }
    },

    /**
     * 提交表单
     */
    async handleSubmit() {
      this.$refs.caseForm.validate(async (valid) => {
        if (!valid) return

        if (this.formData.images.length === 0) {
          this.$message.warning('请至少上传一张案例图片')
          return
        }

        this.uploading = true
        this.uploadProgress = 0

        try {
          // 1. 处理所有需要上传的新图片
          const newImages = this.formData.images.filter(img => img.status === 'new')
          const oldImages = this.formData.images.filter(img => img.status === 'old')

          const uploadedImages = [...oldImages]

          if (newImages.length > 0) {
            this.totalUploadCount = newImages.length
            this.currentUploadIndex = 0

            for (let i = 0; i < newImages.length; i++) {
              const img = newImages[i]
              this.currentUploadIndex = i

              // 使用案例标题作为OSS目录名
              const caseTitle = this.formData.title.trim()
              // 上传时使用imageIndex序号
              const result = await uploadCaseImage(
                caseTitle,
                img.file,
                img.imageIndex,
                (percent) => {
                  // 整体进度 = 已完成部分 + 当前文件进度
                  const baseProgress = (i / newImages.length) * 100
                  this.uploadProgress = Math.round(baseProgress + (percent / newImages.length))
                }
              )

              uploadedImages.push({
                url: result.url,
                fileName: result.fileName,
                status: 'old',
                imageIndex: img.imageIndex
              })
            }

            this.uploadProgress = 100
          }

          // 2. 按当前排序重置 imageIndex（从1开始编号）
          const sortedImages = uploadedImages.map((img, idx) => ({
            url: img.url,
            fileName: img.fileName,
            imageIndex: idx + 1
          }))

          // 构建提交数据（JSON字符串）
          const submitData = {
            title: this.formData.title,
            subtitle: this.formData.subtitle,
            type: this.formData.type,
            projectName: this.formData.projectName,
            projectTime: this.formData.projectTime,
            projectLocation: this.formData.projectLocation,
            projectUrl: this.formData.projectUrl,
            designUnit: this.formData.designUnit,
            constructionUnit: this.formData.constructionUnit,
            description: this.formData.description,
            images: sortedImages
          }

          const jsonStr = JSON.stringify(submitData)

          // 3. 发送到服务器
          if (this.isEdit && this.editId) {
            await updateCase(this.editId, jsonStr)
            this.$message.success('案例更新成功')
          } else {
            await saveCase(jsonStr)
            this.$message.success('案例创建成功')
          }

          this.dialogVisible = false
          this.loadCaseList()
        } catch (error) {
          console.error('保存案例失败:', error)
          const errMsg = error?.response?.data?.error || error.message || '保存案例失败'
          this.$message.error(errMsg)
        } finally {
          this.uploading = false
          this.uploadProgress = 0
          this.currentUploadIndex = 0
          this.totalUploadCount = 0
        }
      })
    },

    /**
     * 删除案例
     */
    handleDelete(row) {
      this.$confirm(
        `确定要删除案例「${row.title}」吗？<br><span style="color:#999;font-size:12px;">将同时删除该案例的所有图片（OSS存储）</span>`,
        '删除确认',
        {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true
        }
      ).then(async () => {
        try {
          // 1. 删除OSS上的案例目录
          await deleteCaseFolder(row.title)
          // 2. 删除服务器数据
          await deleteCase(row.id)
          this.$message.success(`案例「${row.title}」已删除`)
          this.loadCaseList()
        } catch (error) {
          console.error('删除案例失败:', error)
          const errMsg = error?.response?.data?.error || error.message || '删除案例失败'
          this.$message.error(errMsg)
        }
      }).catch(() => {})
    },

    /**
     * 图片加载失败处理
     */
    onImgError(event) {
      event.target.src = ''
      event.target.alt = '图片加载失败'
    }
  }
}
</script>

<style scoped>
.case-management {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e8e8;
}

.page-header h2 {
  margin: 0 0 8px 0;
  font-size: 22px;
  color: #333;
}

.page-header h2 i {
  margin-right: 8px;
  color: #1890ff;
}

.page-header .page-desc {
  margin: 0;
  font-size: 14px;
  color: #999;
}

.action-bar {
  margin-bottom: 16px;
}

.link-text {
  color: #1890ff;
  text-decoration: none;
  font-size: 13px;
}

.link-text:hover {
  text-decoration: underline;
}

.no-data {
  color: #ccc;
}

.image-upload-area {
  width: 100%;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.image-item {
  position: relative;
  width: 120px;
  height: 120px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  background: #fafafa;
  transition: all 0.3s;
}

.image-item:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
}

.image-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.image-item .image-order-actions {
  position: absolute;
  top: 4px;
  left: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-item:hover .image-order-actions {
  opacity: 1;
}

.image-item .image-order-actions .el-button {
  width: 20px;
  height: 20px;
  padding: 0;
  font-size: 10px;
}

.image-item .image-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-item:hover .image-actions {
  opacity: 1;
}

.image-item .image-actions .el-button {
  width: 24px;
  height: 24px;
  padding: 0;
  font-size: 12px;
}

.image-item .image-index {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 11px;
  padding: 2px 4px;
  text-align: center;
}

.image-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #d9d9d9;
  background: #fafafa;
  color: #999;
  font-size: 13px;
  transition: all 0.3s;
}

.image-add:hover {
  border-color: #1890ff;
  color: #1890ff;
  background: #e6f7ff;
}

.image-add i {
  font-size: 28px;
  margin-bottom: 4px;
}

.image-tip {
  margin: 8px 0 0 0;
  font-size: 12px;
  color: #999;
}

.upload-progress-bar {
  padding: 12px 20px;
  background: #fafafa;
  border-top: 1px solid #e8e8e8;
}

.progress-text {
  margin: 6px 0 0 0;
  font-size: 12px;
  color: #999;
  text-align: center;
}
</style>